import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Notify, Confirm, Loading} from 'notiflix';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AddCircle,
  FilterList,
  Refresh,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import {understandItBetterApi} from 'api/understandItBetterApi';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const ENTITY_TYPE_OPTIONS = [
  {value: '', label: 'All Types'},
  {value: 'products', label: 'Product'},
  {value: 'valuepacks', label: 'Value Pack'},
  {value: 'plans', label: 'Question & Answer'},
];

const ENTITY_TYPE_META = {
  products: {label: 'Product', bg: '#e3f2fd', color: '#1565c0'},
  valuepacks: {label: 'Value Pack', bg: '#f3e5f5', color: '#7b1fa2'},
  plans: {label: 'Question & Answer', bg: '#e8f5e9', color: '#2e7d32'},
};

const UnderstandItBetterList = () => {
  const navigate = useNavigate();
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({entityType: '', status: ''});

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await understandItBetterApi.getAllGrouped(filters);
      const list = Array.isArray(data) ? data : [];
      setGroups(list);
    } catch (err) {
      setError(err.message || 'Failed to load sections');
      Notify.failure(err.message || 'Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    fetchGroups();
  }, [filters.entityType, filters.status, canRead]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({...prev, [key]: value}));
  };

  const handleRefresh = () => {
    fetchGroups();
    Notify.info('Refreshing...');
  };

  const handleView = (entityType, entityId) => {
    navigate(`/admin/understand-it-better/view/${entityType}/${entityId}`);
  };

  const handleEdit = (entityType, entityId) => {
    navigate(`/admin/understand-it-better/edit/${entityType}/${entityId}`);
  };

  const handleDelete = (entityType, entityId, entityName) => {
    Confirm.show(
      'Delete All Sections',
      `"${
        entityName || entityId
      }" ke saare sections delete ho jayenge. Kya aap sure hain?`,
      'Delete All',
      'Cancel',
      async () => {
        const key = `${entityType}__${entityId}`;
        setDeletingKey(key);
        Loading.circle('Deleting...');
        try {
          await understandItBetterApi.deleteAllByEntity(entityType, entityId);
          Notify.success('All sections deleted successfully');
          fetchGroups();
        } catch (err) {
          Notify.failure(err.message || 'Failed to delete');
        } finally {
          Loading.remove();
          setDeletingKey(null);
        }
      },
      {okButtonBackground: '#ff6b6b', titleColor: '#ff6b6b'},
    );
  };

  const columns = [
    {
      Header: 'Entity Name',
      accessor: 'entityName',
      width: '25%',
      align: 'left',
    },
    {
      Header: 'Entity Type',
      accessor: 'entityType',
      width: '15%',
      align: 'center',
    },
    {Header: 'Sections', accessor: 'sections', width: '10%', align: 'center'},
    {Header: 'Active', accessor: 'active', width: '10%', align: 'center'},
    {
      Header: 'Titles Preview',
      accessor: 'preview',
      width: '25%',
      align: 'left',
    },
    {
      Header: 'Last Updated',
      accessor: 'updated',
      width: '10%',
      align: 'center',
    },
    {Header: 'Actions', accessor: 'actions', width: '10%', align: 'center'},
  ];

  const getRows = () => {
    if (!Array.isArray(groups) || groups.length === 0) return [];

    return groups.map(group => {
      const key = `${group.entityType}__${group.entityId}`;
      const isDeleting = deletingKey === key;
      const meta = ENTITY_TYPE_META[group.entityType] || {
        label: group.entityType,
        bg: '#f1f5f9',
        color: '#475569',
      };

      // Section titles preview — pehle 2 titles
      const previewTitles = (group.sections || [])
        .slice(0, 2)
        .map(s => s.title);
      const remaining = (group.sections || []).length - 2;

      return {
        entityName: (
          <Box>
            <MDTypography variant="caption" fontWeight="bold" color="text">
              {group.entityName || group.entityId}
            </MDTypography>
            <Typography variant="caption" color="textSecondary" display="block">
              ID: {group.entityId?.slice(0, 8)}...
            </Typography>
          </Box>
        ),

        entityType: (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.bg,
              color: meta.color,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        ),

        sections: (
          <Chip
            label={group.sections?.length || 0}
            size="small"
            sx={{bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700}}
          />
        ),

        active: (
          <Chip
            label={group.activeCount || 0}
            size="small"
            color="success"
            variant="outlined"
          />
        ),

        preview: (
          <Box>
            {previewTitles.map((title, i) => (
              <Typography
                key={i}
                variant="caption"
                color="text"
                display="block"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                }}>
                • {title}
              </Typography>
            ))}
            {remaining > 0 && (
              <Typography variant="caption" color="textSecondary">
                +{remaining} more...
              </Typography>
            )}
          </Box>
        ),

        updated: (
          <Box textAlign="center">
            <MDTypography variant="caption" color="text">
              {group.updatedAt
                ? moment(group.updatedAt).format('MMM D')
                : 'N/A'}
            </MDTypography>
            <Typography variant="caption" color="textSecondary" display="block">
              {group.updatedAt ? moment(group.updatedAt).format('YYYY') : ''}
            </Typography>
          </Box>
        ),

        actions: (
          <Box display="flex" gap={0.5} justifyContent="center">
            {canRead && (
              <Tooltip title="View Sections">
                <IconButton
                  size="small"
                  onClick={() => handleView(group.entityType, group.entityId)}
                  disabled={isDeleting}>
                  <VisibilityIcon color="info" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title="Edit Sections">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(group.entityType, group.entityId)}
                  disabled={isDeleting}>
                  <EditIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete All Sections">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleDelete(
                      group.entityType,
                      group.entityId,
                      group.entityName,
                    )
                  }
                  disabled={isDeleting}>
                  {isDeleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon color="error" fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      };
    });
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to shop management. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  if (loading && groups.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          pt={6}
          pb={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress />
            <Typography variant="h6" mt={2}>
              Loading sections...
            </Typography>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          {/* Header */}
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <MDTypography variant="h6" color="white">
              Understand It Better ({groups.length} entities)
            </MDTypography>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={handleRefresh}
                startIcon={<Refresh />}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  '&:hover': {backgroundColor: 'rgba(255,255,255,0.3)'},
                }}>
                Refresh
              </Button>
              {canCreate && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/admin/understand-it-better/create')}
                  startIcon={<AddCircle />}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    '&:hover': {backgroundColor: 'rgba(255,255,255,0.3)'},
                  }}>
                  Create New
                </Button>
              )}
            </Box>
          </MDBox>

          {/* Filters */}
          <MDBox p={2}>
            <Card sx={{p: 2, mb: 2}}>
              <Box display="flex" alignItems="center" mb={2}>
                <FilterList sx={{mr: 1, color: '#64748b'}} />
                <Typography variant="subtitle1" fontWeight="medium">
                  Filters
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Entity Type</InputLabel>
                    <Select
                      value={filters.entityType}
                      onChange={e =>
                        handleFilterChange('entityType', e.target.value)
                      }
                      label="Entity Type">
                      {ENTITY_TYPE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={e =>
                        handleFilterChange('status', e.target.value)
                      }
                      label="Status">
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </MDBox>

          {/* Error */}
          {error && (
            <MDBox mx={2} mb={2}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </MDBox>
          )}

          {/* Table */}
          <MDBox pt={3}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={6}
                flexDirection="column">
                <CircularProgress />
                <Typography variant="h6" mt={2}>
                  Loading...
                </Typography>
              </Box>
            ) : groups.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={6}
                flexDirection="column">
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No sections found
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  {filters.entityType || filters.status
                    ? 'Try changing your filters'
                    : 'Create your first section to get started'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/admin/understand-it-better/create')}
                  startIcon={<AddCircle />}
                  sx={{color: '#fff'}}>
                  Create Section
                </Button>
              </Box>
            ) : (
              <DataTable
                table={{columns, rows: getRows()}}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default UnderstandItBetterList;

// import React, {useEffect, useState} from 'react';
// import {useNavigate} from 'react-router-dom';
// import {Notify, Confirm, Loading} from 'notiflix';
// import moment from 'moment';
// import {
//   Box,
//   Button,
//   Card,
//   Chip,
//   IconButton,
//   Tooltip,
//   Typography,
//   CircularProgress,
//   Alert,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   Grid,
// } from '@mui/material';
// import {
//   Edit as EditIcon,
//   Delete as DeleteIcon,
//   Visibility as VisibilityIcon,
//   AddCircle,
//   FilterList,
//   Refresh,
// } from '@mui/icons-material';
// import MDBox from 'components/MDBox';
// import MDTypography from 'components/MDTypography';
// import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
// import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
// import DataTable from 'examples/Tables/DataTable';
// import {understandItBetterApi} from 'api/understandItBetterApi'; // aapka API file

// // ─── Constants ───────────────────────────────────────────────────────────────
// const ENTITY_TYPE_OPTIONS = [
//   {value: '', label: 'All Types'},
//   {value: 'product', label: 'Product'},
//   {value: 'valuepack', label: 'Value Pack'},
//   {value: 'question_answer', label: 'Question & Answer'},
// ];

// const ENTITY_TYPE_LABELS = {
//   product: 'Product',
//   valuepack: 'Value Pack',
//   question_answer: 'Question & Answer',
// };

// const ENTITY_TYPE_COLORS = {
//   product: {bg: '#e3f2fd', color: '#1565c0'},
//   valuepack: {bg: '#f3e5f5', color: '#7b1fa2'},
//   question_answer: {bg: '#e8f5e9', color: '#2e7d32'},
// };

// // ─── Component ────────────────────────────────────────────────────────────────
// const UnderstandItBetterList = () => {
//   const navigate = useNavigate();

//   const [sections, setSections] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [deletingId, setDeletingId] = useState(null);
//   const [error, setError] = useState(null);

//   const [filters, setFilters] = useState({
//     entityType: '',
//     status: '',
//     page: 1,
//     limit: 10,
//   });

//   const [stats, setStats] = useState({
//     total: 0,
//     active: 0,
//     product: 0,
//     valuepack: 0,
//     question_answer: 0,
//   });

//   const fetchSections = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const response = await understandItBetterApi.getAll(filters);
//       const data = Array.isArray(response.data)
//         ? response.data
//         : Array.isArray(response)
//         ? response
//         : [];

//       setSections(data);

//       setStats({
//         total: data.length,
//         active: data.filter(s => s.status !== false).length,
//         product: data.filter(s => s.entityType === 'product').length,
//         valuepack: data.filter(s => s.entityType === 'valuepack').length,
//         question_answer: data.filter(s => s.entityType === 'question_answer')
//           .length,
//       });
//     } catch (err) {
//       console.error('Error fetching sections:', err);
//       console.error('Response data:', err.response?.data);
//       setError(err.message || 'Failed to load sections');
//       Notify.failure(err.message || 'Failed to load sections');
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchSections();
//   }, [filters.entityType, filters.status, filters.page]);

//   // ─── Handlers ───────────────────────────────────────────────────────────────
//   const handleDelete = async id => {
//     Confirm.show(
//       'Delete Section',
//       'Are you sure you want to delete this section? This action cannot be undone.',
//       'Delete',
//       'Cancel',
//       async () => {
//         setDeletingId(id);
//         Loading.circle('Deleting section...');
//         try {
//           await understandItBetterApi.deleteOne(id);
//           Notify.success('Section deleted successfully');
//           fetchSections();
//         } catch (err) {
//           Notify.failure(err.message || 'Failed to delete section');
//         } finally {
//           Loading.remove();
//           setDeletingId(null);
//         }
//       },
//       {okButtonBackground: '#ff6b6b', titleColor: '#ff6b6b'},
//     );
//   };

//   const handleView = id => navigate(`/admin/understand-it-better/${id}`);
//   const handleEdit = id => navigate(`/admin/understand-it-better/edit/${id}`);

//   const handleFilterChange = (key, value) => {
//     setFilters(prev => ({...prev, [key]: value, page: 1}));
//   };

//   const handleRefresh = () => {
//     fetchSections();
//     Notify.info('Refreshing list...');
//   };

//   // ─── Table config ────────────────────────────────────────────────────────────
//   const columns = [
//     {Header: 'Title', accessor: 'title', width: '22%', align: 'left'},
//     {
//       Header: 'Entity Type',
//       accessor: 'entityType',
//       width: '15%',
//       align: 'center',
//     },
//     {
//       Header: 'Entity Name',
//       accessor: 'entityName',
//       width: '18%',
//       align: 'left',
//     },
//     {
//       Header: 'Description',
//       accessor: 'description',
//       width: '22%',
//       align: 'left',
//     },
//     {Header: 'Status', accessor: 'status', width: '8%', align: 'center'},
//     {Header: 'Created', accessor: 'created', width: '10%', align: 'center'},
//     {Header: 'Actions', accessor: 'actions', width: '10%', align: 'center'},
//   ];

//   const getRows = () => {
//     if (!Array.isArray(sections)) return [];

//     return sections.map(section => {
//       const isDeleting = deletingId === section.id;
//       const entityColor = ENTITY_TYPE_COLORS[section.entityType] || {
//         bg: '#f5f5f5',
//         color: '#616161',
//       };

//       return {
//         title: (
//           <MDTypography variant="caption" fontWeight="medium" color="text">
//             {section.title}
//           </MDTypography>
//         ),
//         entityType: (
//           <Chip
//             label={
//               ENTITY_TYPE_LABELS[section.entityType] ||
//               section.entityType ||
//               'N/A'
//             }
//             size="small"
//             sx={{
//               backgroundColor: entityColor.bg,
//               color: entityColor.color,
//               fontWeight: 600,
//               fontSize: '0.7rem',
//             }}
//           />
//         ),
//         entityName: (
//           <MDTypography variant="caption" color="text">
//             {section.entityName || section.entityId || 'N/A'}
//           </MDTypography>
//         ),
//         description: (
//           <Tooltip title={section.description || ''}>
//             <MDTypography
//               variant="caption"
//               color="text"
//               sx={{
//                 display: '-webkit-box',
//                 WebkitLineClamp: 2,
//                 WebkitBoxOrient: 'vertical',
//                 overflow: 'hidden',
//                 maxWidth: 260,
//               }}>
//               {section.description || '—'}
//             </MDTypography>
//           </Tooltip>
//         ),
//         status: (
//           <Chip
//             label={section.status !== false ? 'Active' : 'Inactive'}
//             size="small"
//             color={section.status !== false ? 'success' : 'error'}
//             variant={section.status !== false ? 'filled' : 'outlined'}
//           />
//         ),
//         created: (
//           <Box textAlign="center">
//             <MDTypography variant="caption" color="text">
//               {section.createdAt
//                 ? moment(section.createdAt).format('MMM D')
//                 : 'N/A'}
//             </MDTypography>
//             <Typography variant="caption" color="textSecondary" display="block">
//               {section.createdAt
//                 ? moment(section.createdAt).format('YYYY')
//                 : ''}
//             </Typography>
//           </Box>
//         ),
//         actions: (
//           <Box display="flex" gap={0.5} justifyContent="center">
//             <Tooltip title="View Details">
//               <IconButton
//                 size="small"
//                 onClick={() => handleView(section.id)}
//                 disabled={isDeleting}>
//                 <VisibilityIcon color="info" fontSize="small" />
//               </IconButton>
//             </Tooltip>
//             <Tooltip title="Edit">
//               <IconButton
//                 size="small"
//                 onClick={() => handleEdit(section.id)}
//                 disabled={isDeleting}>
//                 <EditIcon color="primary" fontSize="small" />
//               </IconButton>
//             </Tooltip>
//             <Tooltip title="Delete">
//               <IconButton
//                 size="small"
//                 onClick={() => handleDelete(section.id)}
//                 disabled={isDeleting}>
//                 {isDeleting ? (
//                   <CircularProgress size={16} />
//                 ) : (
//                   <DeleteIcon color="error" fontSize="small" />
//                 )}
//               </IconButton>
//             </Tooltip>
//           </Box>
//         ),
//       };
//     });
//   };

//   // ─── Loading screen ──────────────────────────────────────────────────────────
//   if (loading && sections.length === 0) {
//     return (
//       <DashboardLayout>
//         <DashboardNavbar />
//         <Box
//           pt={6}
//           pb={3}
//           display="flex"
//           justifyContent="center"
//           alignItems="center"
//           minHeight="50vh">
//           <Box textAlign="center">
//             <CircularProgress />
//             <Typography variant="h6" mt={2}>
//               Loading sections...
//             </Typography>
//           </Box>
//         </Box>
//       </DashboardLayout>
//     );
//   }

//   // ─── Render ──────────────────────────────────────────────────────────────────
//   return (
//     <DashboardLayout>
//       <DashboardNavbar />
//       <MDBox pt={6} pb={3}>
//         <Card>
//           {/* Header */}
//           <MDBox
//             mx={2}
//             mt={-3}
//             py={3}
//             px={2}
//             variant="gradient"
//             bgColor="info"
//             borderRadius="lg"
//             coloredShadow="info"
//             display="flex"
//             justifyContent="space-between"
//             alignItems="center">
//             <MDTypography variant="h6" color="white">
//               Understand It Better Sections ({stats.total})
//             </MDTypography>

//             <Box display="flex" gap={2}>
//               <Button
//                 variant="contained"
//                 onClick={handleRefresh}
//                 startIcon={<Refresh />}
//                 sx={{
//                   backgroundColor: 'rgba(255,255,255,0.2)',
//                   color: '#fff',
//                   '&:hover': {backgroundColor: 'rgba(255,255,255,0.3)'},
//                 }}>
//                 Refresh
//               </Button>
//               <Button
//                 variant="contained"
//                 onClick={() => navigate('/admin/understand-it-better/create')}
//                 startIcon={<AddCircle />}
//                 sx={{
//                   backgroundColor: 'rgba(255,255,255,0.2)',
//                   color: '#fff',
//                   '&:hover': {backgroundColor: 'rgba(255,255,255,0.3)'},
//                 }}>
//                 Create New
//               </Button>
//             </Box>
//           </MDBox>

//           {/* Filters */}
//           <MDBox p={2}>
//             <Card sx={{p: 2, mb: 2}}>
//               <Box display="flex" alignItems="center" mb={2}>
//                 <FilterList sx={{mr: 1, color: '#64748b'}} />
//                 <Typography variant="subtitle1" fontWeight="medium">
//                   Filters
//                 </Typography>
//               </Box>
//               <Grid container spacing={2}>
//                 <Grid item xs={12} sm={6} md={4}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Entity Type</InputLabel>
//                     <Select
//                       value={filters.entityType}
//                       onChange={e =>
//                         handleFilterChange('entityType', e.target.value)
//                       }
//                       label="Entity Type">
//                       {ENTITY_TYPE_OPTIONS.map(opt => (
//                         <MenuItem key={opt.value} value={opt.value}>
//                           {opt.label}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} sm={6} md={4}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Status</InputLabel>
//                     <Select
//                       value={filters.status}
//                       onChange={e =>
//                         handleFilterChange('status', e.target.value)
//                       }
//                       label="Status">
//                       <MenuItem value="">All</MenuItem>
//                       <MenuItem value="true">Active</MenuItem>
//                       <MenuItem value="false">Inactive</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </Grid>
//               </Grid>
//             </Card>
//           </MDBox>

//           {/* Error */}
//           {error && (
//             <MDBox mx={2} mb={2}>
//               <Alert severity="error" onClose={() => setError(null)}>
//                 {error}
//               </Alert>
//             </MDBox>
//           )}

//           {/* Table */}
//           <MDBox pt={3}>
//             {loading ? (
//               <Box
//                 display="flex"
//                 justifyContent="center"
//                 alignItems="center"
//                 py={6}
//                 flexDirection="column">
//                 <CircularProgress />
//                 <Typography variant="h6" mt={2}>
//                   Loading...
//                 </Typography>
//               </Box>
//             ) : sections.length === 0 ? (
//               <Box
//                 display="flex"
//                 justifyContent="center"
//                 alignItems="center"
//                 py={6}
//                 flexDirection="column">
//                 <Typography variant="h6" color="textSecondary" gutterBottom>
//                   No sections found
//                 </Typography>
//                 <Typography variant="body2" color="textSecondary" mb={2}>
//                   {filters.entityType || filters.status
//                     ? 'Try changing your filters'
//                     : 'Create your first section to get started'}
//                 </Typography>
//                 <Button
//                   variant="contained"
//                   color="primary"
//                   onClick={() => navigate('/admin/understand-it-better/create')}
//                   startIcon={<AddCircle />}
//                   sx={{color: '#fff'}}>
//                   Create Section
//                 </Button>
//               </Box>
//             ) : (
//               <DataTable
//                 table={{columns, rows: getRows()}}
//                 isSorted={false}
//                 entriesPerPage={false}
//                 showTotalEntries={false}
//                 noEndBorder
//               />
//             )}

//             {/* Pagination */}
//             {sections.length > 0 && (
//               <MDBox
//                 display="flex"
//                 justifyContent="space-between"
//                 alignItems="center"
//                 p={2}>
//                 <Typography variant="body2" color="textSecondary">
//                   Showing {sections.length} of {stats.total} sections
//                 </Typography>
//                 <Box display="flex" gap={1}>
//                   <Button
//                     size="small"
//                     disabled={filters.page <= 1}
//                     onClick={() =>
//                       handleFilterChange('page', filters.page - 1)
//                     }>
//                     Previous
//                   </Button>
//                   <Button
//                     size="small"
//                     disabled={sections.length < filters.limit}
//                     onClick={() =>
//                       handleFilterChange('page', filters.page + 1)
//                     }>
//                     Next
//                   </Button>
//                 </Box>
//               </MDBox>
//             )}
//           </MDBox>
//         </Card>
//       </MDBox>
//     </DashboardLayout>
//   );
// };

// export default UnderstandItBetterList;
